<template>
  <div id="app" class="container-fluid">
    <full_head />

    <div class="main-content">
      <div class="row g-3">
        <!-- Left Column - Personal Information -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h3>
                <i class="fas fa-user-circle"></i>
                Personal Info
              </h3>
              <button class="btn-edit-header" @click="showEditProfileModal">
                <i class="fas fa-edit"></i>
                Edit Profile
              </button>
            </div>
            <div class="card-body">
              <div class="info-list">
                <div class="info-row">
                  <span class="label">Full Name</span>
                  <span class="value">{{ patient.fullName }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Age</span>
                  <span class="value">{{ patient.age }} yrs</span>
                </div>
                <div class="info-row">
                  <span class="label">Date of Birth</span>
                  <span class="value">{{ patient.dateOfBirth }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Marital Status</span>
                  <span class="value">{{ patient.maritalStatus }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Education</span>
                  <span class="value">{{ patient.education }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Occupation</span>
                  <span class="value">{{ patient.occupation }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Husband's Name</span>
                  <span class="value">{{ patient.husbandName }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Husband's Job</span>
                  <span class="value">{{ patient.husbandOccupation }}</span>
                </div>
                <div class="info-row info-row-full">
                  <span class="label">Address</span>
                  <span class="value">{{ patient.address }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Phone</span>
                  <span class="value">{{ patient.phone }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Emergency Contact</span>
                  <span class="value">{{ patient.emergencyContact }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Obstetric History -->
        <div class="col-lg-6">
          <!-- Current Pregnancy Card -->
          <div class="card">
            <div class="card-header">
              <h3>
                <i class="fas fa-calendar-check"></i>
                Current Pregnancy
              </h3>
            </div>
            <div class="card-body">
              <div class="pregnancy-stats">
                <div class="stat-item">
                  <span class="stat-label">LMP</span>
                  <span class="stat-value">{{ pregnancyInfo.lmp }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">EDD</span>
                  <span class="stat-value">{{ pregnancyInfo.edd }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Gestational Age</span>
                  <span class="stat-value">{{ pregnancyInfo.gestationalAge }} wks</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">ANC Visits</span>
                  <span class="stat-value">{{ pregnancyInfo.ancVisits }}</span>
                </div>
              </div>
              
              <div class="pregnancy-details">
                <div style="border-right: 1px solid grey;" class="detail-row">
                  <span class="detail-label">Blood Group</span>
                  <span class="detail-value">{{ pregnancyInfo.bloodGroup }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Rhesus Factor</span>
                  <span class="detail-value">{{ pregnancyInfo.rhesusFactor }}</span>
                </div>
                <div  style="border-right: 1px solid grey;" class="detail-row">
                  <span class="detail-label">HIV Status</span>
                  <span class="detail-value">
                    <span :class="pregnancyInfo.hivStatus === 'Negative' ? 'status-negative' : 'status-positive'">
                      {{ pregnancyInfo.hivStatus }}
                    </span>
                  </span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">VDRL/RPR</span>
                  <span class="detail-value">{{ pregnancyInfo.vdrl }}</span>
                </div>
                <div  style="border-right: 1px solid grey;" class="detail-row">
                  <span class="detail-label">HB Level</span>
                  <span class="detail-value">{{ pregnancyInfo.hbLevel }} g/dL</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Tetanus Doses</span>
                  <span class="detail-value">{{ pregnancyInfo.tetanusCompleted }}/{{ pregnancyInfo.tetanusTotal }}</span>
                </div>
                <div  style="border-right: 1px solid grey;" class="detail-row">
                  <span class="detail-label">Last Visit</span>
                  <span class="detail-value">{{ pregnancyInfo.lastVisit }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Next Appointment</span>
                  <span class="detail-value">{{ pregnancyInfo.nextAppointment }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Obstetric History Card -->
          <div class="card">
            <div class="card-header">
              <h3>
                <i class="fas fa-baby-carriage"></i>
                Obstetric History
              </h3>
            </div>
            <div class="card-body">
              <div class="gp-score">
                <div class="gp-item">
                  <span class="gp-label">Gravida</span>
                  <span class="gp-value">{{ obstetricHistory.gravida }}</span>
                </div>
                <div class="gp-item">
                  <span class="gp-label">Para</span>
                  <span class="gp-value">{{ obstetricHistory.para }}</span>
                </div>
                <div class="gp-item">
                  <span class="gp-label">Abortions</span>
                  <span class="gp-value">{{ obstetricHistory.abortions }}</span>
                </div>
                <div class="gp-item">
                  <span class="gp-label">Living</span>
                  <span class="gp-value">{{ obstetricHistory.livingChildren }}</span>
                </div>
              </div>

              <h4 class="section-title">Previous Pregnancies</h4>
              <div v-if="obstetricHistory.previousPregnancies.length" class="table-responsive">
                <table class="table-custom">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Outcome</th>
                      <th>Weeks</th>
                      <th>Weight</th>
                      <th>Delivery</th>
                      <th>Complications</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(pregnancy, index) in obstetricHistory.previousPregnancies" :key="index">
                      <td>{{ pregnancy.year }}</td>
                      <td>
                        <span class="badge-outcome" :class="getOutcomeClass(pregnancy.outcome)">
                          {{ pregnancy.outcome }}
                        </span>
                      </td>
                      <td>{{ pregnancy.gestation }} wks</td>
                      <td>{{ pregnancy.birthWeight }} kg</td>
                      <td>{{ pregnancy.deliveryMode }}</td>
                      <td>
                        <span v-if="pregnancy.complications" class="complication-badge">
                          <i class="fas fa-exclamation-triangle"></i> Yes
                        </span>
                        <span v-else class="text-muted">None</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="no-data">No previous pregnancy history</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <anc_profile_editor v-if="showEditModal" @close-edit-patient="showEditModal = false" />
</template>

<script>
import full_head from "./full_head.vue";
import anc_profile_editor from "./anc_profile_editor.vue";

export default {
  name: "MaternalProfile",
  components: { full_head, anc_profile_editor },
  data() {
    return {
      showEditModal: false,
      patient: {
        fullName: "Sarah Johnson",
        age: 32,
        dateOfBirth: "1991-05-15",
        maritalStatus: "Married",
        education: "Bachelor's Degree",
        occupation: "Teacher",
        husbandName: "Michael Johnson",
        husbandOccupation: "Engineer",
        address: "123 Main Street, Springfield, IL 62704",
        phone: "(555) 123-4567",
        emergencyContact: "(555) 987-6543 (Husband)",
      },
      pregnancyInfo: {
        lmp: "2023-06-15",
        edd: "2024-03-22",
        gestationalAge: 28,
        ancVisits: 5,
        bloodGroup: "O",
        rhesusFactor: "Positive",
        hivStatus: "Negative",
        vdrl: "Non-reactive",
        hbLevel: 11.5,
        tetanusCompleted: 2,
        tetanusTotal: 5,
        lastVisit: "2023-11-15",
        nextAppointment: "2023-12-15",
      },
      obstetricHistory: {
        gravida: 3,
        para: 2,
        abortions: 0,
        livingChildren: 2,
        previousPregnancies: [
          {
            year: 2020,
            outcome: "Live Birth",
            gestation: 39,
            birthWeight: "3.2",
            deliveryMode: "Cesarean Section",
            complications: true,
          },
          {
            year: 2022,
            outcome: "Live Birth",
            gestation: 40,
            birthWeight: "3.5",
            deliveryMode: "Vaginal",
            complications: false,
          },
        ],
      },
    };
  },
  methods: {
    showEditProfileModal() {
      this.showEditModal = true;
    },
    getOutcomeClass(outcome) {
      const classes = {
        "Live Birth": "outcome-live",
        Stillbirth: "outcome-stillbirth",
        Miscarriage: "outcome-miscarriage",
        Abortion: "outcome-miscarriage",
      };
      return classes[outcome] || "";
    },
  },
};
</script>

<style scoped>
/* Reset & Base */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.container-fluid {
  padding: 12px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.main-content {
  margin-top: 12px;
}

/* Cards */
.card {
  background: white;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

/* Card Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 2px solid #dee2e6;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-header h3 i {
  color: #667eea;
  font-size: 18px;
}

/* Edit Button in Header */
.btn-edit-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 7px 16px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  white-space: nowrap;
}

.btn-edit-header:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
}

.btn-edit-header:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(102, 126, 234, 0.2);
}

.btn-edit-header i {
  font-size: 12px;
}

/* Card Body */
.card-body {
  padding: 16px 20px;
}

/* Info List */
.info-list {
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f3f4;
  gap: 15px;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row-full {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.info-row-full .value {
  text-align: left;
}

.label {
  color: #6c757d;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  min-width: 130px;
}

.value {
  color: #212529;
  font-size: 13px;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
}

/* Pregnancy Stats */
.pregnancy-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.stat-item {
  text-align: center;
  padding: 12px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
}

.stat-label {
  display: block;
  font-size: 10px;
  font-weight: 500;
  opacity: 0.9;
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
}

/* Pregnancy Details */
.pregnancy-details {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f3f4;
  gap: 10px;
}

.detail-label {
  color: #6c757d;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.detail-value {
  color: #212529;
  font-size: 13px;
  font-weight: 500;
  text-align: right;
}

.status-negative {
  color: #28a745;
  font-weight: 600;
}

.status-positive {
  color: #dc3545;
  font-weight: 600;
}

/* GP Score */
.gp-score {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 20px;
}

.gp-item {
  text-align: center;
  padding: 14px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  transition: transform 0.2s ease;
}

.gp-item:hover {
  transform: translateY(-2px);
}

.gp-label {
  display: block;
  font-size: 11px;
  font-weight: 500;
  opacity: 0.9;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.gp-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

/* Section Title */
.section-title {
  font-size: 14px;
  color: #495057;
  margin-bottom: 10px;
  font-weight: 600;
  padding-bottom: 6px;
  border-bottom: 2px solid #f1f3f4;
}

/* Custom Table */
.table-responsive {
  overflow-x: auto;
}

.table-custom {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.table-custom th {
  background: #f8f9fa;
  padding: 10px 8px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.table-custom td {
  padding: 8px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
}

.table-custom tbody tr:hover {
  background: #f8f9fa;
}

/* Outcome Badges */
.badge-outcome {
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.outcome-live {
  background: #d4edda;
  color: #155724;
}

.outcome-stillbirth {
  background: #f8d7da;
  color: #721c24;
}

.outcome-miscarriage {
  background: #fff3cd;
  color: #856404;
}

/* Complication Badge */
.complication-badge {
  color: #dc3545;
  font-weight: 600;
  font-size: 11px;
}

.complication-badge i {
  margin-right: 3px;
}

/* No Data */
.no-data {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 6px;
}

/* Responsive */
@media (max-width: 768px) {
  .card-header {
    padding: 10px 14px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .card-header h3 {
    font-size: 15px;
  }

  .btn-edit-header {
    padding: 6px 14px;
    font-size: 11px;
    width: 100%;
    justify-content: center;
  }

  .card-body {
    padding: 12px 14px;
  }

  .info-row {
    padding: 6px 8px;
    gap: 12px;
  }

  .label {
    font-size: 11px;
    min-width: 100px;
  }

  .value {
    font-size: 12px;
  }

  .pregnancy-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }

  .pregnancy-details {
    grid-template-columns: 1fr;
  }

  .gp-score {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }

  .gp-item {
    padding: 12px 6px;
  }

  .gp-value {
    font-size: 20px;
  }

  .table-custom th,
  .table-custom td {
    padding: 6px;
    font-size: 11px;
  }
}

@media (max-width: 576px) {
  .container-fluid {
    padding: 8px;
  }

  .stat-value {
    font-size: 14px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }

  .value {
    text-align: left;
  }
}
</style>